import React from "react";

export const cx = (...c) => c.filter(Boolean).join(" ");

const baseInput =
  "w-full bg-[#0d120e] border border-[#232f25] rounded-md px-3.5 py-2.5 text-sm text-[#f4f7f4] placeholder:text-[#69776b] outline-none transition-colors focus:border-[#8CE63C] focus:ring-2 focus:ring-[#8CE63C]/20";

export const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-semibold tracking-wide uppercase text-[#9EABA0] mb-1.5">
    {children}
    {required && <span className="text-[#8CE63C] ml-1">*</span>}
  </label>
);

export const TextField = ({ label, required, testId, error, hint, ...props }) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <input data-testid={testId} className={cx(baseInput, error && "border-red-500/70")} {...props} />
    {hint && <p className="text-[11px] text-[#69776b] mt-1">{hint}</p>}
    {error && <p className="text-[11px] text-red-400 mt-1">{error}</p>}
  </div>
);

export const TextArea = ({ label, required, testId, rows = 4, ...props }) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <textarea data-testid={testId} rows={rows} className={cx(baseInput, "resize-y")} {...props} />
  </div>
);

export const SelectField = ({ label, required, testId, options, placeholder = "Select…", ...props }) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <select data-testid={testId} className={cx(baseInput, "appearance-none cursor-pointer")} {...props}>
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#111712]">
          {o}
        </option>
      ))}
    </select>
  </div>
);

export const RadioRow = ({ label, required, options, value, onChange, testIdPrefix }) => (
  <div>
    {label && <FieldLabel required={required}>{label}</FieldLabel>}
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value === o;
        return (
          <button
            key={o}
            type="button"
            data-testid={testIdPrefix ? `${testIdPrefix}-${o.toLowerCase().replace(/\s+/g, "-")}` : undefined}
            onClick={() => onChange(o)}
            className={cx(
              "px-4 py-2 rounded-md text-sm font-medium border transition-all",
              active
                ? "bg-[#8CE63C] border-[#8CE63C] text-[#080b08]"
                : "bg-[#0d120e] border-[#232f25] text-[#9EABA0] hover:border-[#8CE63C]/50 hover:text-[#f4f7f4]"
            )}
          >
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

export const CheckboxGrid = ({ options, selected, onToggle, testIdPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
    {options.map((o) => {
      const active = selected.includes(o);
      return (
        <button
          key={o}
          type="button"
          data-testid={`${testIdPrefix}${o.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onToggle(o)}
          className={cx(
            "flex items-center gap-3 px-4 py-3 rounded-md border text-sm font-medium text-left transition-all",
            active
              ? "bg-[#132717] border-[#8CE63C] text-[#f4f7f4] erp-lime-glow"
              : "bg-[#0d120e] border-[#232f25] text-[#9EABA0] hover:border-[#8CE63C]/50"
          )}
        >
          <span
            className={cx(
              "h-4 w-4 rounded-sm border flex items-center justify-center shrink-0",
              active ? "bg-[#8CE63C] border-[#8CE63C]" : "border-[#3a4a3c]"
            )}
          >
            {active && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#080b08" strokeWidth="4">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </span>
          {o}
        </button>
      );
    })}
  </div>
);

export const SectionCard = ({ index, title, desc, children }) => (
  <section className="erp-fade-up rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden">
    <div className="flex items-center gap-4 px-5 sm:px-7 py-4 border-b border-[#232f25] bg-[#0d120e]">
      <span className="font-mono text-sm font-bold text-[#080b08] bg-[#8CE63C] rounded-md h-8 w-8 flex items-center justify-center shrink-0">
        {String(index).padStart(2, "0")}
      </span>
      <div>
        <h3 className="font-display text-lg sm:text-xl font-bold text-white tracking-tight">{title}</h3>
        {desc && <p className="text-xs text-[#69776b] mt-0.5">{desc}</p>}
      </div>
    </div>
    <div className="p-5 sm:p-7 space-y-5">{children}</div>
  </section>
);
